import { useCallback, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import DateStamp from "@components/dates/DateStamp";
import styles from "@components/forms/careProfile/CareProfile.module.css";
import Nbsp from "@components/Nbsp";
import currencies from "@i18n/currencies";
import { CareProfileChangeFieldEnum, CareProfileChangeKindEnum, GetCareProfileChangesQuery } from "@typing/Generated";
import { languageNameForCode } from "@utils/languageUtils";
import { snakeToCamel } from "@utils/stringUtils";

type CareProfileChangeType = NonNullable<
  NonNullable<NonNullable<GetCareProfileChangesQuery["careProfileChanges"]["edges"]>[number]>["node"]
>;

type CareProfileChangeResourceType = CareProfileChangeType["resource"];

type Props = {
  change: CareProfileChangeType;
};

type CareProfileChangeValueProps = {
  field: CareProfileChangeFieldEnum;
  resource: CareProfileChangeResourceType;
  value: any;
};

const CareProfileChangeModificationValue = ({ field, resource, value }: CareProfileChangeValueProps) => {
  if (value === undefined || value === null || (Array.isArray(value) && value.length === 0)) {
    return "blank";
  }

  if (value === "") return <FormattedMessage id="models.careProfile.emptyStringValue" />;

  if (Array.isArray(value)) {
    const items = value.map((v, idx) => (
      <span key={v}>
        <CareProfileChangeModificationValue field={field} resource={resource} value={v} />
        {idx < value.length - 1 && (
          <>
            <FormattedMessage id="dictionary.punctuation.comma" />
            <Nbsp />
          </>
        )}
      </span>
    ));
    return items;
  }

  if (
    [
      CareProfileChangeFieldEnum.DATE,
      CareProfileChangeFieldEnum.DATE_OF_BIRTH,
      CareProfileChangeFieldEnum.DATE_OF_DEATH,
      CareProfileChangeFieldEnum.EXACT_AGE_ENTERED_AT,
      CareProfileChangeFieldEnum.PREGNANCY_DUE_DATE,
      CareProfileChangeFieldEnum.UPDATE_DATE
    ].includes(field)
  ) {
    return <DateStamp absoluteDate value={new Date(value)} />;
  } else if (
    [
      CareProfileChangeFieldEnum.IS_DECEASED,
      CareProfileChangeFieldEnum.IS_VETERAN,
      CareProfileChangeFieldEnum.IS_VETERAN_GUARDIAN,
      CareProfileChangeFieldEnum.IS_VETERAN_PARTNER,
      CareProfileChangeFieldEnum.LEGAL_HELP_NEEDED,
      CareProfileChangeFieldEnum.LEGALLY_DISABLED,
      CareProfileChangeFieldEnum.RECENT_HOSPITALIZATION,
      CareProfileChangeFieldEnum.VA_CONNECTED
    ].includes(field)
  ) {
    return value ? <FormattedMessage id="dictionary.yes" /> : <FormattedMessage id="dictionary.no" />;
  } else if (
    [
      CareProfileChangeFieldEnum.STAGE_BATHING_AND_DRESSING,
      CareProfileChangeFieldEnum.STAGE_CONTINENCE,
      CareProfileChangeFieldEnum.STAGE_EATING,
      CareProfileChangeFieldEnum.STAGE_HOUSEKEEPING,
      CareProfileChangeFieldEnum.STAGE_MEAL_PREP,
      CareProfileChangeFieldEnum.STAGE_SHOPPING,
      CareProfileChangeFieldEnum.STAGE_TOILETING,
      CareProfileChangeFieldEnum.STAGE_TRANSPORTATION
    ].includes(field)
  ) {
    return <FormattedMessage id={`models.careProfile.stageLimited.${value}`} />;
  } else if (
    [
      CareProfileChangeFieldEnum.EVER_BEEN_CAREGIVER,
      CareProfileChangeFieldEnum.MEDICATIONS_AFFORDABLE,
      CareProfileChangeFieldEnum.HIGH_RISK_PREGNANCY,
      CareProfileChangeFieldEnum.RECENT_INCIDENT,
      CareProfileChangeFieldEnum.DAY_TO_DAY_SUPPORT_NEEDED,
      CareProfileChangeFieldEnum.LIFE_THREATENING_CONDITION,
      CareProfileChangeFieldEnum.LONG_TERM_CONDITION
    ].includes(field)
  ) {
    return <FormattedMessage id={`models.careProfile.yesNoDontKnowOptions.${value}`} />;
  } else if (
    [
      CareProfileChangeFieldEnum.CAREGIVING_IMPACT_FINANCIAL,
      CareProfileChangeFieldEnum.CAREGIVING_IMPACT_PHYSICAL,
      CareProfileChangeFieldEnum.CAREGIVING_IMPACT_WORK
    ].includes(field)
  ) {
    return <FormattedMessage id={`models.careProfile.caregivingImpactOptions.${value}`} />;
  } else if (field === CareProfileChangeFieldEnum.HOUSEHOLD_INCOME_CURRENCY_CODE) {
    return currencies[value];
  } else if (field === CareProfileChangeFieldEnum.CONTROL) {
    return <FormattedMessage id={`models.diagnosis.controlOptions.${value}`} />;
  } else if (field === CareProfileChangeFieldEnum.FORMAT) {
    return <FormattedMessage id={`models.medication.formatOptions.${value}`} />;
  } else if (field === CareProfileChangeFieldEnum.GUARDIAN_EMPLOYMENT_STATUS) {
    return <FormattedMessage id={`models.careProfile.employmentStatusOptions.${value}`} />;
  } else if (field === CareProfileChangeFieldEnum.GUARDIAN_MARITAL_STATUS) {
    return <FormattedMessage id={`models.careProfile.maritalStatusOptions.${value}`} />;
  } else if (field === CareProfileChangeFieldEnum.KIND && resource.__typename === "LegalDocument") {
    return <FormattedMessage id={`models.legalDocument.kindOptions.${value}`} />;
  } else if (field === CareProfileChangeFieldEnum.KIND && resource.__typename === "Medication") {
    return <FormattedMessage id={`models.medication.kindOptions.${value}`} />;
  } else if (field === CareProfileChangeFieldEnum.KIND && resource.__typename === "Provider") {
    return <FormattedMessage id={`models.provider.kindOptions.${value}`} />;
  } else if (field === CareProfileChangeFieldEnum.LANGUAGE_CODE) {
    return languageNameForCode(value);
  } else if (field === CareProfileChangeFieldEnum.PAIN_SEVERITY) {
    return <FormattedMessage id={`models.careProfile.severityOptions.${value}`} />;
  } else if (field === CareProfileChangeFieldEnum.PAYMENT_TYPE) {
    return <FormattedMessage id={`models.medication.paymentTypeOptions.${value}`} />;
  } else if (field === CareProfileChangeFieldEnum.RELATIONSHIP) {
    return <FormattedMessage id={`models.clientRole.relationship.${value}`} />;
  } else if (field === CareProfileChangeFieldEnum.STAGE) {
    return <FormattedMessage id={`models.journey.stage.${value}`} />;
  } else if (field === CareProfileChangeFieldEnum.STATUS) {
    return <FormattedMessage id={`models.legalDocument.statusOptions.${value}`} />;
  } else if (
    [
      CareProfileChangeFieldEnum.ACUITY,
      CareProfileChangeFieldEnum.AGE,
      CareProfileChangeFieldEnum.CARE_TYPES_PROVIDED,
      CareProfileChangeFieldEnum.CAREGIVER_TYPES,
      CareProfileChangeFieldEnum.CAREGIVING_DURATION,
      CareProfileChangeFieldEnum.CAREGIVING_TIME_PER_WEEK,
      CareProfileChangeFieldEnum.COGNITIVE_STATUSES,
      CareProfileChangeFieldEnum.CONSERVATORSHIP_STATUS,
      CareProfileChangeFieldEnum.CURRENT_CARE_TRANSITIONS,
      CareProfileChangeFieldEnum.DEVELOPMENTAL_OR_EDUCATIONAL_CONCERNS,
      CareProfileChangeFieldEnum.DISTANCE_TO_LOVED_ONE,
      CareProfileChangeFieldEnum.DURABLE_MEDICAL_EQUIPMENT,
      CareProfileChangeFieldEnum.EMOTIONAL_CONCERNS,
      CareProfileChangeFieldEnum.EMPLOYMENT_STATUS,
      CareProfileChangeFieldEnum.EVER_BEEN_CAREGIVER,
      CareProfileChangeFieldEnum.FALL_COUNT,
      CareProfileChangeFieldEnum.FINANCIAL_MANAGER,
      CareProfileChangeFieldEnum.MARITAL_STATUS,
      CareProfileChangeFieldEnum.GUARDIAN_TYPE,
      CareProfileChangeFieldEnum.HEALTH_INSURANCE_TYPES,
      CareProfileChangeFieldEnum.HOUSEHOLD_INCOME_LEVEL,
      CareProfileChangeFieldEnum.IMMIGRATION_STATUS,
      CareProfileChangeFieldEnum.INCOME_SOURCES,
      CareProfileChangeFieldEnum.INSURANCE_TYPES,
      CareProfileChangeFieldEnum.MEDICATION_MANAGEMENT_STATUS,
      CareProfileChangeFieldEnum.PRONOUNS,
      CareProfileChangeFieldEnum.PROVIDER_EMOTIONAL_STATE,
      CareProfileChangeFieldEnum.PROVIDER_FEELS_SUPPORTED,
      CareProfileChangeFieldEnum.PROVIDER_FINANCIAL_CONCERNS,
      CareProfileChangeFieldEnum.RELIGION,
      CareProfileChangeFieldEnum.RESIDENCE_TYPE,
      CareProfileChangeFieldEnum.SEVERITY,
      CareProfileChangeFieldEnum.SENSE_OF_FULFILLMENT,
      CareProfileChangeFieldEnum.SOCIAL_SATISFACTION,
      CareProfileChangeFieldEnum.STABILITY_SUPPORTS,
      CareProfileChangeFieldEnum.TYPES_OF_SUPPORT_NEEDED,
      CareProfileChangeFieldEnum.USE_CASE
    ].includes(field)
  ) {
    return <FormattedMessage id={`models.careProfile.${snakeToCamel(field)}Options.${value}`} />;
  } else {
    return value;
  }
};

const CareProfileChange = ({ change }: Props) => {
  const intl = useIntl();

  const fieldDescription = useMemo(() => {
    if (change.kind === CareProfileChangeKindEnum.MODIFICATION) {
      const camel = snakeToCamel(change.field as string);

      if (change.resource.__typename === "Client") {
        return (
          <>
            <FormattedMessage id={`forms.careProfileNew.${camel}Label`} />
            <Nbsp />
            <FormattedMessage id="models.careProfileChange.forName" values={{ name: change.resource.fullName }} />
          </>
        );
      } else if (change.resource.__typename === "ClientRole") {
        return (
          <>
            <FormattedMessage id={`forms.careProfileNew.${camel}Label`} />
            <Nbsp />
            <FormattedMessage
              id="models.careProfileChange.forName"
              values={{ name: change.resource.client.fullName }}
            />
          </>
        );
      } else if (change.resource.__typename === "Diagnosis") {
        const field = intl.formatMessage({ id: `forms.diagnosis.${camel}Label` });

        return (
          <FormattedMessage
            id="models.careProfileChange.diagnosisChangeDescription"
            values={{
              condition: change.resource.medicalCondition?.consumerName ?? change.resource.conditionOther,
              field
            }}
          />
        );
      } else if (change.resource.__typename === "LegalDocument") {
        const field = intl.formatMessage({ id: `forms.legalDocument.${camel}Label` });

        const kind = intl.formatMessage({ id: `models.legalDocument.kindOptions.${change.resource.kind}` });

        return (
          <FormattedMessage id="models.careProfileChange.legalDocumentChangeDescription" values={{ field, kind }} />
        );
      } else if (change.resource.__typename === "Location") {
        if (change.resource.locationCareProfile?.name) {
          return (
            <FormattedMessage
              id="models.careProfileChange.locationChangeDescription"
              values={{ name: change.resource.locationCareProfile.name }}
            />
          );
        } else if (change.resource.locationClient?.fullName) {
          return (
            <FormattedMessage
              id="models.careProfileChange.locationChangeDescription"
              values={{ name: change.resource.locationClient.fullName }}
            />
          );
        }
      } else if (change.resource.__typename === "Medication") {
        const field = intl.formatMessage({ id: `forms.medication.${camel}Label` });

        return (
          <FormattedMessage
            id="models.careProfileChange.medicationChangeDescription"
            values={{ field, name: change.resource.name }}
          />
        );
      } else if (change.resource.__typename === "Provider") {
        const field = intl.formatMessage({ id: `forms.provider.${camel}Label` });

        return (
          <FormattedMessage
            id="models.careProfileChange.providerChangeDescription"
            values={{ field, name: change.resource.name }}
          />
        );
      }

      return <FormattedMessage id={`forms.careProfileNew.${camel}Label`} />;
    } else if (change.kind === CareProfileChangeKindEnum.CREATION) {
      if (change.resource.__typename === "Email") {
        return (
          <FormattedMessage
            id="models.careProfileChange.emailAddressChangeDescription"
            values={{ name: change.resource.user.fullName }}
          />
        );
      } else if (change.resource.__typename === "PhoneNumber") {
        return (
          <FormattedMessage
            id="models.careProfileChange.phoneNumberChangeDescription"
            values={{ name: change.resource.client.fullName }}
          />
        );
      }
    }

    if (change.kind === CareProfileChangeKindEnum.CREATION) {
      return <FormattedMessage id={`models.careProfileChange.added.${change.resource.__typename}`} />;
    } else if (change.kind === CareProfileChangeKindEnum.DELETION) {
      return <FormattedMessage id={`models.careProfileChange.deleted.${change.resource.__typename}`} />;
    }
  }, [change.field, change.kind, change.resource, intl]);

  const attributeLabelId = useCallback(
    (attribute: string) => {
      const camelAttribute = snakeToCamel(attribute);

      if (change.resource.__typename === "Diagnosis") {
        return `forms.diagnosis.${camelAttribute}Label`;
      } else if (change.resource.__typename === "Email") {
        return `models.careProfileChange.emailAttributes.${camelAttribute}`;
      } else if (change.resource.__typename === "Medication") {
        return `forms.medication.${camelAttribute}Label`;
      } else if (change.resource.__typename === "PhoneNumber") {
        return `models.careProfileChange.phoneNumberAttributes.${camelAttribute}`;
      } else if (change.resource.__typename === "Provider") {
        return `forms.provider.${camelAttribute}Label`;
      } else if (change.resource.__typename === "LegalDocument") {
        return `forms.legalDocument.${camelAttribute}Label`;
      }
    },
    [change.resource.__typename]
  );

  const changeDescription = useMemo(() => {
    const data = JSON.parse(change.changeJson);

    if (change.kind === CareProfileChangeKindEnum.MODIFICATION) {
      return (
        <div className={styles.careProfileChangeDescriptionRow}>
          <div>
            <FormattedMessage id="models.careProfileChange.changedFrom" />
          </div>
          <div className={styles.careProfileChangeValue}>
            <CareProfileChangeModificationValue
              field={change.field as CareProfileChangeFieldEnum}
              resource={change.resource}
              value={data["from"]}
            />
          </div>
          <div>
            <FormattedMessage id="models.careProfileChange.to" />
          </div>
          <div className={styles.careProfileChangeValue}>
            <CareProfileChangeModificationValue
              field={change.field as CareProfileChangeFieldEnum}
              resource={change.resource}
              value={data["to"]}
            />
          </div>
        </div>
      );
    }

    const useKeys = Object.keys(data).filter(key => {
      const d = data[key];
      return d !== undefined && d !== null;
    });

    return (
      <div className={styles.careProfileChangeAddedOrDeletedAttributes}>
        {useKeys.map(key => (
          <div key={key}>
            <div className={styles.careProfileChangeAddedOrDeletedAttribute}>
              <span className={styles.careProfileChangeAddedOrDeletedAttributeName}>
                <FormattedMessage id={attributeLabelId(key)} />
              </span>
              <span className={styles.careProfileChangeValue}>
                <CareProfileChangeModificationValue
                  field={key as CareProfileChangeFieldEnum}
                  resource={change.resource}
                  value={data[key]}
                />
              </span>
            </div>
          </div>
        ))}
      </div>
    );
  }, [attributeLabelId, change.changeJson, change.field, change.kind, change.resource]);

  return (
    <div className={styles.careProfileChange}>
      <div>{fieldDescription}</div>
      <div>{changeDescription}</div>
      <div>
        <DateStamp value={change.date} />
      </div>
    </div>
  );
};

export default CareProfileChange;
